/**
 * Järjestelmän vakiot. Nämä ovat samat frontendissä ja backendissä —
 * kahtena kopiona ne eriytyisivät puolessa vuodessa.
 */
import { DEFAULT_BRAND_NAME } from './branding.js';
import { DEFAULT_CURRENCY } from './currency.js';
/** Kirjauksen tarkkuus minuutteina (suunnitelma 4.1). */
export const MINUTE_STEP = 30;
/** Kirjauksen enimmäiskesto vuorokaudessa. */
export const MAX_MINUTES_PER_ENTRY = 24 * 60;
/** Pikavalinnat kirjauslomakkeen kestokentässä, tunteina. */
export const DURATION_PRESETS_HOURS = [0.5, 1, 2, 4, 7.5];
/** Kuvauskentän enimmäispituus. */
export const MAX_DESCRIPTION_LENGTH = 500;
/** Kuukauden avaus vaatii perustelun (suunnitelma 6.3). */
export const MIN_UNLOCK_REASON_LENGTH = 10;
/** Viikko alkaa maanantaista, ISO 8601. */
export const WEEK_STARTS_ON_MONDAY = true;
export const APP_TIME_ZONE = 'Europe/Helsinki';
export const APP_LOCALE = 'fi-FI';
/**
 * Tuontierän yläraja (suunnitelma 9.6). Suurempi erä pilkotaan, ja
 * jokainen erä on oma transaktionsa — osittain tuotu erä olisi
 * migraatiossa pahempi kuin epäonnistunut.
 */
export const MAX_IMPORT_ROWS = 5000;
/**
 * Vientitiedostojen muotoversio (suunnitelma 11).
 *
 * 2: asiakkaan ja projektin attribuutit `totals`-riveillä, ja CSV:n
 * sarakejoukko valittavissa — jolloin `kuvaus` ei enää ole oletuksena
 * mukana. Vastaanottaja, joka luki sarakkeita paikan mukaan, tarvitsee
 * tämän numeron erottaakseen vanhan tiedoston uudesta.
 */
export const EXPORT_SCHEMA_VERSION = 2;
/**
 * Agenttitunnuksen päiväikkuna (backlog 5): kuinka monta kokonaista
 * kuukautta taakse ja päivää eteenpäin nykyhetkestä tunnus näkee ja saa
 * ehdottaa.
 *
 * Ikkuna on turva, ei mukavuus. Kirjoitustyökalu, joka luo laskutusrivejä
 * ja jota ajaa repon sisältöä lukeva agentti, on kehotusinjektion kohde:
 * ikkuna rajaa sen, mitä hyökkäys parhaimmillaankin saa aikaan.
 *
 * Taakse ikkuna alkaa **edellisen kuukauden ensimmäisestä päivästä**.
 * Tunnit kirjataan kuukausittain, usein vasta kuun vaihteen jälkeen, ja
 * kiinteä päivämäärä (aiemmin 14) katkaisi juuri sen kuukauden, jota
 * käyttäjä oli kirjaamassa. Laajennus on turvallinen, koska kirjoitus on
 * vain ehdotus, jonka ihminen vahvistaa, ja lukitun kuukauden ehdotus
 * torjutaan erikseen: käytännössä ikkuna on kuluva kuu ja edellinen kuu
 * siihen asti, kunnes se lukitaan. Viikko eteenpäin riittää sovittuun
 * poissaoloon.
 */
export const AGENT_WINDOW_MONTHS_PAST = 1;
export const AGENT_WINDOW_DAYS_FUTURE = 7;
/**
 * MCP-palvelimen tukemat protokollaversiot, uusin ensin (backlog 5).
 *
 * Lista on olemassa, koska kättelyssä on neuvoteltava: asiakas kertoo
 * haluamansa version, ja palvelin vastaa joko samalla tai omalla
 * uusimmallaan. Kiinteä vastaus rikkoisi vanhemman asiakkaan hiljaisesti.
 */
export const MCP_PROTOCOL_VERSIONS = ['2025-06-18', '2025-03-26'];
/** Ehdotuksen perustelun enimmäispituus. */
export const MAX_SUGGESTION_RATIONALE_LENGTH = 1000;
/**
 * Asetusavaimet ja niiden oletukset (suunnitelma 7).
 * Arvot ovat merkkijonoja, koska desimaaleja ei käsitellä liukulukuina.
 */
/**
 * Muistutuslajit (backlog 3.1). Vastaa kannan `ReminderKind`-enumia rivi
 * riviltä.
 *
 * Tämä on jaetussa paketissa eikä palvelimella, koska hallintanäkymä
 * asettaa muistutusten aikataulun ja tarvitsee saman listan. Kaksi listaa
 * eri paikoissa eroaisi ensimmäisellä lisäyksellä.
 */
export const REMINDER_KINDS = [
    'WEEKLY_ENTRIES_MISSING',
    'MONTH_UNCHECKED_CUSTOMERS',
    'MONTH_NOT_LOCKED',
    'TRAINING_OVERDUE',
    'TASK_BLOCKED_OVERDUE',
    'DEVELOPMENT_REVIEW_DUE',
    'CERTIFICATION_EXPIRING',
    'MILESTONE_DUE',
];
/**
 * Kunkin lajin aikataulun laji. Kiinteä, ei asetus: "viikon tunnit ovat
 * kirjaamatta" on viikkoa koskeva väite, eikä siitä tule kuukausittaista
 * sillä, että ajopäiväksi valitsee kuukauden päivän.
 */
export const REMINDER_SCHEDULE_KIND = {
    WEEKLY_ENTRIES_MISSING: 'WEEKLY',
    MONTH_UNCHECKED_CUSTOMERS: 'MONTHLY',
    MONTH_NOT_LOCKED: 'MONTHLY',
    /**
     * Kuukausittain, vaikka koulutus ei ole kuukauden asia.
     *
     * Puuttuva koulutus ei vanhene viikossa eikä korjaannu päivässä:
     * viikoittainen muistutus samasta asiasta olisi neljä viestiä kuussa,
     * ja neljäs opettaisi jättämään ne avaamatta.
     */
    TRAINING_OVERDUE: 'MONTHLY',
    /**
     * Joka arkipäivä, koska jumiutunut tehtävä ylittää rajansa minä päivänä
     * tahansa. Viesti ei silti lähde joka päivä: valinta (`reminders.ts`)
     * poimii tehtävän rajan ylityspäivänä ja sen jälkeen viikon välein,
     * ja viikonloppuun osuva päivä siirtyy maanantaihin (PROJEKTINHALLINTA 8).
     */
    TASK_BLOCKED_OVERDUE: 'DAILY',
    /**
     * Kuukausittain, samasta syystä kuin koulutus: kehityskeskustelu ei
     * korjaannu viikossa. Puolen vuoden kadenssilla neljä viestiä kuussa
     * olisi kolme liikaa.
     */
    DEVELOPMENT_REVIEW_DUE: 'MONTHLY',
    /**
     * Kuukausittain. Vanheneminen on 90 päivän ikkuna, joten kolme
     * muistutusta kerkiää ennen määräpäivää — ja se on juuri se määrä, joka
     * riittää varaamaan tenttiajan ilman että viestistä tulee taustakohinaa.
     */
    CERTIFICATION_EXPIRING: 'MONTHLY',
    /**
     * Joka arkipäivä kuten jumissa-muistutus: milestonen eräpäivä on mikä
     * päivä tahansa. Valinta poimii milestonen kerran ennen eräpäivää ja
     * myöhästyneen viikon välein (PROJEKTINHALLINTA 26).
     */
    MILESTONE_DUE: 'DAILY',
};
/**
 * Muistutusten oletukset. Käytössä, kunnes ylläpitäjä muuttaa niitä
 * Hallinta → Asetukset -näkymästä.
 *
 * Järjestys 3.–10. ennen 8.–15. on kuun vaihteen työnkulku: asiakasvastaava
 * tarkistaa, ADMIN lukitsee. Toisin päin lukitusmuistutus pyytäisi
 * lukitsemaan aineiston, jota kukaan ei ole vielä katsonut.
 */
export const REMINDER_DEFAULTS = {
    WEEKLY_ENTRIES_MISSING: {
        enabled: true,
        // Perjantai: viikko on tehty muttei vielä ohi.
        schedule: { kind: 'WEEKLY', weekday: 5 },
        extraText: null,
    },
    MONTH_UNCHECKED_CUSTOMERS: {
        enabled: true,
        schedule: { kind: 'MONTHLY', dayFrom: 3, dayTo: 10 },
        extraText: null,
    },
    MONTH_NOT_LOCKED: {
        enabled: true,
        schedule: { kind: 'MONTHLY', dayFrom: 8, dayTo: 15 },
        extraText: null,
    },
    /**
     * Kuun alussa, ennen kuun vaihteen kiireitä.
     *
     * Koulutus on se työ, joka siirtyy aina huomiseen, ja kuun ensimmäinen
     * viikko on ainoa hetki, jolloin huominen ei ole vielä varattu
     * tarkistuksille ja lukituksille.
     */
    TRAINING_OVERDUE: {
        enabled: true,
        schedule: { kind: 'MONTHLY', dayFrom: 1, dayTo: 7 },
        extraText: null,
    },
    TASK_BLOCKED_OVERDUE: {
        enabled: true,
        schedule: { kind: 'DAILY' },
        extraText: null,
    },
    /**
     * Kuun alussa kuten koulutusmuistutus, ja **samassa ikkunassa**
     * tarkoituksella: esihenkilö saa molemmat henkilöstöä koskevat
     * muistutuksensa samana aamuna eikä kahtena eri päivänä.
     */
    DEVELOPMENT_REVIEW_DUE: {
        enabled: true,
        schedule: { kind: 'MONTHLY', dayFrom: 1, dayTo: 7 },
        extraText: null,
    },
    CERTIFICATION_EXPIRING: {
        enabled: true,
        schedule: { kind: 'MONTHLY', dayFrom: 1, dayTo: 7 },
        extraText: null,
    },
    MILESTONE_DUE: {
        enabled: true,
        schedule: { kind: 'DAILY' },
        extraText: null,
    },
};
/**
 * Kuukausimuistutuksen viimeinen sallittu ajopäivä.
 *
 * 28 eikä 31: helmikuussa ei ole 29. päivää joka vuosi, ja ikkuna, joka
 * katoaa kerran neljässä vuodessa, on ansa. Se ei kaataisi mitään — viesti
 * vain jäisi lähtemättä eikä kukaan huomaisi.
 */
export const MAX_REMINDER_DAY_OF_MONTH = 28;
export const SETTING_DEFAULTS = {
    workdayHours: '7.5',
    defaultWeeklyHours: '37.5',
    currency: DEFAULT_CURRENCY,
    brandName: DEFAULT_BRAND_NAME,
    csvDialect: 'excel-fi',
    allowedEmailDomains: ['devikone.com'],
    lockChecklistMinFillRate: '0.8',
    budgetWarnThreshold: '0.9',
    budgetScheduleGapWarn: '0.15',
    maxReportRangeMonths: 24,
    reminders: REMINDER_DEFAULTS,
    /**
     * Kehityskeskustelujen säilytysaika kuukausina tilin passivoinnista
     * (docs/KEHITYSSUUNNITELMAT.md, luku 11).
     *
     * Keskustelut ja tavoitteet ovat henkilötietoa, jonka käsittelyperuste
     * on työsuhde. Työsuhteen päätyttyä peruste lakkaa, ja tiedon pitää
     * poistua sovitun ajan kuluttua. **Osaamisrekisteri jää**: "meillä oli
     * tuolloin kolme AZ-104-osaajaa" on väite, joka pitää voida todentaa
     * senkin jälkeen, kun yksi heistä on lähtenyt.
     *
     * Kaksi vuotta on se aika, jonka kuluessa lähteneen työntekijän
     * kehityskeskusteluun voi vielä olla asiallinen syy palata
     * (työtodistus, riita työsuhteen ehdoista); sen jälkeen ei ole.
     * Päätetty 15.9.2026.
     *
     * `0` = ei automaattista poistoa. Se on kelvollinen valinta vain, jos
     * poisto hoidetaan muuten — ei siksi, ettei säilytysaikaa ole päätetty.
     */
    developmentRetentionMonths: 24,
    /**
     * Tyyppi levennetään merkkijonoksi tarkoituksella: tämä on ainoa
     * asetuksista, jota käyttöliittymä kirjoittaa vapaana lukuna, ja
     * `as const` sitoisi sen literaaliin `"0.70"`.
     */
    billableTargetShare: '0.70',
};
/**
 * Laskutettavan työn tavoite (osuus 0–1).
 *
 * Järjestelmän oletus on `SETTING_DEFAULTS.billableTargetShare`; yksittäiselle
 * työntekijälle voidaan asettaa oma tavoite, joka voittaa sen. Tavoite ja
 * sen toteuma ovat **vain ADMINille**: kyse on henkilön työn arvioinnista
 * eikä laskutusaineistosta, eikä sitä siksi näytetä edes raportoijalle.
 *
 * Mittari on **laskutusaste**: laskutettavat tunnit jaettuna sillä työajalla,
 * joka jaksosta on tähän mennessä kulunut. Nimittäjä ei ole kirjatut tunnit —
 * muuten alikirjaaminen nostaisi tavoitteen täyttymistä, ja neljä tuntia
 * päivässä pelkkää laskutettavaa olisi 100 %.
 */
export const MIN_BILLABLE_TARGET_SHARE = 0;
export const MAX_BILLABLE_TARGET_SHARE = 1;
/** Sisäisen asiakkaan koodi (suunnitelma 4.5). */
export const INTERNAL_CUSTOMER_CODE = 'DEVIKONE';
/**
 * Virhekoodit. Käyttöliittymä voi reagoida koodiin; viesti on aina
 * suomeksi ja kertoo mitä tehdä (suunnitelma 8).
 */
export const ERROR_CODES = {
    MONTH_LOCKED: 'MONTH_LOCKED',
    TASK_TYPE_CUSTOMER_MISMATCH: 'TASK_TYPE_CUSTOMER_MISMATCH',
    PROJECT_CUSTOMER_MISMATCH: 'PROJECT_CUSTOMER_MISMATCH',
    INVALID_MINUTES_STEP: 'INVALID_MINUTES_STEP',
    INTERNAL_CUSTOMER_NOT_BILLABLE: 'INTERNAL_CUSTOMER_NOT_BILLABLE',
    INTERNAL_CUSTOMER_NOT_ASSIGNABLE: 'INTERNAL_CUSTOMER_NOT_ASSIGNABLE',
    RATE_NOT_RESOLVED: 'RATE_NOT_RESOLVED',
    UNLOCK_REASON_REQUIRED: 'UNLOCK_REASON_REQUIRED',
    IMPORT_VALIDATION_FAILED: 'IMPORT_VALIDATION_FAILED',
    RANGE_TOO_LONG: 'RANGE_TOO_LONG',
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    /** Agenttitunnuksen päiväikkunan ulkopuolinen pyyntö (backlog 5). */
    OUTSIDE_AGENT_WINDOW: 'OUTSIDE_AGENT_WINDOW',
    /**
     * Tenttiä ei voi juuri nyt aloittaa (docs/KOULUTUKSET.md).
     *
     * Oma koodinsa eikä pelkkä 409, koska käyttöliittymä tekee eri asian
     * jokaisesta syystä: kesken oleva tentti avataan, jäähy näytetään
     * kellona, lukematta jäänyt materiaali on linkki takaisin osioihin.
     * Syy kulkee `details.reason`issa.
     */
    EXAM_NOT_AVAILABLE: 'EXAM_NOT_AVAILABLE',
    /** Tenttiyritys on umpeutunut: aika loppui tai sessio päättyi. */
    EXAM_EXPIRED: 'EXAM_EXPIRED',
    /**
     * Suljetun projektin tai passivoidun asiakkaan taulu on lukutilassa
     * (docs/PROJEKTINHALLINTA.md, luku 4). Oma koodinsa, jotta käyttöliittymä
     * osaa sanoa "taulu on lukittu" eikä "ristiriita".
     */
    BOARD_READ_ONLY: 'BOARD_READ_ONLY',
    /**
     * Salaisuuksien jako (docs/SALAISUUDET.md, luku 16). Omat koodinsa,
     * koska vastaanottajan sivu — ja asiakkaan skripti — tekee jokaisesta
     * eri asian: PIN-kenttä, jäljellä olevat yritykset, "jo avattu".
     *
     * `SECRET_NOT_FOUND` kattaa sekä tuntemattoman tunnisteen että väärän
     * avaimen. Ero on tarkoituksella näkymätön.
     */
    SECRET_NOT_FOUND: 'SECRET_NOT_FOUND',
    SECRET_GONE: 'SECRET_GONE',
    SECRET_PIN_REQUIRED: 'SECRET_PIN_REQUIRED',
    SECRET_PIN_INVALID: 'SECRET_PIN_INVALID',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
// --- Projektinhallinta (docs/PROJEKTINHALLINTA.md) --------------------------
/**
 * Uimaradan kiinteät osiot järjestyksessä. Vastaa kannan
 * `KanbanColumn`-enumia rivi riviltä.
 *
 * Osiot ovat enum eivätkä rivejä, jotta ne ovat olemassa heti jokaisella
 * uimaradalla eikä niitä voi siirtää tai poistaa — rakenne tekee sen,
 * mitä oikeustarkistus muuten joutuisi tekemään (luku 5).
 */
/**
 * IN_REVIEW on DOING-osion ja DONE-osion välissä: tekijä — ihminen tai
 * agentti — siirtää tehtävän sinne, kun se on hänen mielestään valmis, ja
 * DONE-osioon sen siirtää joku muu. Agentti ei siirrä DONE-osioon koskaan
 * (PROJEKTINHALLINTA 14).
 */
export const KANBAN_COLUMNS = [
    'INFO',
    'TODO',
    'DOING',
    'BLOCKED',
    'IN_REVIEW',
    'DONE',
    'SKIPPED',
];
/**
 * Päättyneet osiot: DONE on tehty, SKIPPED on päätetty jättää tekemättä
 * (PROJEKTINHALLINTA 5). Molemmat lasketaan ratkaistuiksi riippuvuuksissa
 * ja molemmat hiljennetään taululla. SKIPPED-osioon siirto vaatii
 * perustelun kommenttina, ja se on ihmisen päätös — agentti ei siirrä sinne.
 */
export const KANBAN_RESOLVED_COLUMNS = ['DONE', 'SKIPPED'];
/** Uimaradan tila (PROJEKTINHALLINTA 5). Vastaa kannan `LaneStatus`-enumia. */
export const LANE_STATUSES = ['PLANNED', 'ACTIVE', 'DONE'];
/** Hiljennetyssä osiossa näkyviä tehtäviä ennen "näytä kaikki" -painiketta. */
export const KANBAN_COLLAPSED_VISIBLE = 5;
/**
 * DOING-osion hälytysraja: enemmän kuin näin monta tehtävää samalle
 * vastuuhenkilölle koko taululla. Merkki, ei lukko.
 */
export const DOING_WIP_LIMIT = 3;
/** Työmääräarvion koot. Koko, ei aika: ei muunnu tunneiksi (luku 6). */
export const TASK_ESTIMATES = ['XS', 'S', 'M', 'L', 'XL'];
/**
 * Deadlinen siirron syy (PROJEKTINHALLINTA 29): kenen takia päivä siirtyi
 * — me, asiakas vai kumppani. Valinnainen; ensimmäisellä asetuksella ei
 * ole syytä, koska mitään ei siirretty. Vastaa kannan `KanbanDueCause`-enumia.
 */
export const KANBAN_DUE_CAUSES = ['OWN', 'CUSTOMER', 'PARTNER'];
/**
 * Montako vuorokautta BLOCKED-osiossa ennen huutomerkkiä (luku 8).
 * Käyttöliittymän raja, ei kannan: muutos ei vaadi migraatiota.
 */
export const BLOCKED_ALERT_DAYS = 8;
export const KANBAN_LANE_NAME_MAX = 120;
export const KANBAN_TASK_TITLE_MAX = 200;
export const KANBAN_TASK_DESCRIPTION_MAX = 10_000;
/** Tehtävän kommentti: mitä tehtiin tai huomattiin, ei kuinka kauan (6). */
export const KANBAN_COMMENT_MAX = 4_000;
/** Uimaradan milestone (PROJEKTINHALLINTA 26). */
export const KANBAN_MILESTONE_NAME_MAX = 120;
export const KANBAN_MILESTONE_DESCRIPTION_MAX = 2_000;
/** Kumppanirekisteri (PROJEKTINHALLINTA 29). */
export const PARTNER_NAME_MAX = 120;
export const PARTNER_DESCRIPTION_MAX = 2_000;
/**
 * Kuinka monta kalenteripäivää ennen eräpäivää milestonen muistutus lähtee
 * (PROJEKTINHALLINTA 26). Viikonloppuun osuva päivä siirtyy edeltävään
 * perjantaihin: ennakkovaroitus on hyödyllinen vain ennen eräpäivää.
 */
export const MILESTONE_ALERT_DAYS = 3;
/** Asiakasraportin valmistuneiden jakso, päivinä (PROJEKTINHALLINTA 27). */
export const CUSTOMER_REPORT_PERIODS = [7, 14, 30];
/**
 * Kommentin reaktiot (PROJEKTINHALLINTA 22). Neljä, joilla jokaisella on
 * sovittu merkitys; vapaata emojivalikoimaa ei ole, koska "samaa mieltä"
 * ja "nähty" ovat vastauksia eivätkä koristeita. Vastaa kannan
 * `ReactionEmoji`-enumia; merkit ovat käyttöliittymän asia.
 */
export const REACTION_EMOJIS = ['THUMBS_UP', 'CHECK', 'EYES', 'RED_FLAG'];
/**
 * Tehtävien relaatiot (PROJEKTINHALLINTA 6). Vastaa kannan
 * `KanbanRelationKind`-enumia. Suunnatun lajin vastapuolella näkyy eri
 * sana (`KANBAN_RELATION_INVERSE`); symmetrinen näkyy samana molemmin puolin.
 */
export const KANBAN_RELATION_KINDS = [
    'DEPENDS_ON',
    'DUPLICATE_OF',
    'RELATES_TO',
    'FOLLOWS',
];
export const KANBAN_RELATION_SYMMETRIC = {
    DEPENDS_ON: false,
    DUPLICATE_OF: false,
    RELATES_TO: true,
    FOLLOWS: false,
};
/** Tehtävähaun tulosten enimmäismäärä relaation lisäyksessä. */
export const KANBAN_TASK_SEARCH_LIMIT = 20;
/**
 * Taulukohtaiset notifikaatioasetukset (PROJEKTINHALLINTA 15). Puuttuva
 * asetusrivi tarkoittaa näitä. Jumissa-muistutus on oletuksena päällä,
 * koska se on talon yhteinen sopimus; tapahtumaviestit pois, koska
 * jokainen siirto sähköpostina opettaa jättämään viestit avaamatta.
 */
export const KANBAN_BOARD_SETTING_DEFAULTS = {
    blockedReminder: true,
    notifyBlocked: false,
    notifyAssigned: false,
    notifyMention: false,
    milestoneReminder: true,
};
//# sourceMappingURL=constants.js.map